import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, tap } from "rxjs";
import { LoaderService } from "./loader.service";
import { ActionResult, RACE_COMMERCIAL_ITEM_DEFAULT, RaceAdditionalPricingItem, RaceCommercialItem } from "@model";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class RaceCommercialService {
  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService
  ) {}

  private fetchItems<T>(url: string, hideLoading?: boolean): Observable<T[]> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.get<ActionResult<T[]>>(url).pipe(
      tap(() => !hideLoading && this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  findRaceCommercialItems(raceId: number, hideLoading?: boolean): Observable<RaceCommercialItem[]> {
    return this.fetchItems<RaceCommercialItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/race/fetch?raceId=${raceId}`,
      hideLoading
    );
  }

  findRaceCommercialItemsByEventId(eventId: number, hideLoading?: boolean): Observable<RaceCommercialItem[]> {
    return this.fetchItems<RaceCommercialItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/event/fetch?eventId=${eventId}`,
      hideLoading
    );
  }


  calculateRaceCommercialItemsPrice(raceId: number, couponCode?: string): Observable<RaceCommercialItem[]> {
    return this.fetchItems<RaceCommercialItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/race/calculate?raceId=${raceId}${couponCode ? `&couponCode=${couponCode}` : ''}`
    );
  }

  updateRaceCommercialItem(item: RaceCommercialItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/${item.id ? "api/pricing/update" : "api/pricing/add"}`, item
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(
      result => result.resultCode !== -1 ? onSuccess() : onError()
    );
  }

  deleteRaceCommercialItem(item: RaceCommercialItem, onSuccess: () => void, onError: (errorCode: number) => void): void {
    this.loaderService.startLoading();
    this.http.delete<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/pricing/delete?itemId=${item.id}`
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(
      result => result.resultCode !== -1 && result.resultCode !== RaceCommercialServiceErrors.COMMERCIAL_ITEM_IN_USE ? onSuccess() : onError(result.resultCode)
    );
  }

  sortRaceCommercialItem(items: RaceCommercialItem[] = []): RaceCommercialItem[] {
    const defaultItem = items.find(item => item.registrationFeeTypeId === RACE_COMMERCIAL_ITEM_DEFAULT);
    return defaultItem ? [defaultItem, ...items.filter(item => item.registrationFeeTypeId !== RACE_COMMERCIAL_ITEM_DEFAULT)] : items;
  }

  findRaceAdditionalPricingItems(raceId: number, hideLoading?: boolean): Observable<RaceAdditionalPricingItem[]> {
    return this.fetchItems<RaceAdditionalPricingItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/items/race/fetch?raceId=${raceId}`,
      hideLoading
    );
  }

  findEventAdditionalPricingItems(eventId: number, hideLoading?: boolean): Observable<RaceAdditionalPricingItem[]> {
    return this.fetchItems<RaceAdditionalPricingItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/items/event/fetch?eventId=${eventId}`,
      hideLoading
    );
  }

  calculateRaceAdditionalPricingPrice(raceId: number, couponCode?: string): Observable<RaceAdditionalPricingItem[]> {
    return this.fetchItems<RaceAdditionalPricingItem>(
      `${this.environmentService.getApiUrl()}/api/public/pricing/items/race/calculate?raceId=${raceId}${couponCode ? `&couponCode=${couponCode}` : ''}`,
    );
  }

  updateRaceAdditionalPricingItem(item: RaceAdditionalPricingItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/${item.id ? "api/pricing/items/update" : "api/pricing/items/add"}`, item
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(
      result => result.resultCode !== -1 ? onSuccess() : onError()
    );
  }

  deleteRaceAdditionalPricingItem(item: RaceAdditionalPricingItem, onSuccess: () => void, onError: (errorCode: number) => void): void {
    this.loaderService.startLoading();
    this.http.delete<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/pricing/items/delete?itemId=${item.id}`
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(
      result => result.resultCode !== -1 && result.resultCode !== RaceCommercialServiceErrors.ADDITIONAL_PRICING_ITEM_IN_USE ? onSuccess() : onError(result.resultCode)
    );
  }
}


export enum RaceCommercialServiceErrors {
  COMMERCIAL_ITEM_IN_USE = 17,
  ADDITIONAL_PRICING_ITEM_IN_USE = 18
}
